import { useAuthStore } from '@/stores/authStore';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { usePlanStore } from '@/stores/planStore';
import { isSubscriptionActive } from '@/lib/subscription';

export type SetupGateReason =
  /** Plan state not read yet — nothing truthful to say about the blocker. */
  | 'checking'
  /** The automation pipeline is under way; the page unlocks on its own. */
  | 'running'
  /** The pipeline failed and will not resume without a retry. */
  | 'failed'
  /** Onboarding is answered but automation cannot start without an active plan. */
  | 'needs_plan'
  /** No plan problem, so the wizard itself is what is unfinished. */
  | 'wizard_incomplete';

export interface SetupGateState {
  /**
   * True while the backend will reject guarded endpoints. A gated page must
   * skip its fetches entirely rather than catch the rejection afterwards.
   */
  isGated: boolean;
  /** Why setup is unfinished, which decides what the page offers the user. */
  reason: SetupGateReason;
}

/**
 * The one gate for "this page needs onboarding to be finished".
 *
 * The backend runs `OnboardingGuard` globally and default-deny: while
 * `onboarding.completed` is false, every endpoint without `@SkipOnboarding`
 * returns 403 ONBOARDING_INCOMPLETE. `completed` only flips at the very end of
 * the automation pipeline, so it stays false for the whole run — and forever
 * if the run fails or never starts.
 *
 * The reason matters as much as the gate. `OnboardingFlow` bails out before
 * `startAutomation()` when the subscription is inactive, so a user can finish
 * every wizard step and still have automation sitting at `not_started`. Reading
 * that as "they never onboarded" put a Resume Onboarding button in front of
 * people who were actually just unsubscribed, dropping them back on a step they
 * had already completed (LIL-440).
 */
export function useSetupGate(): SetupGateState {
  const hasCompletedOnboarding = useAuthStore(s => s.hasCompletedOnboarding);
  const planHydrated = usePlanStore(s => s.hydrated);
  const subscription = usePlanStore(s => s.subscription);
  const automationReason = useOnboardingStore((s): 'running' | 'failed' | null => {
    const { isRunning, error, stages } = s.automation;
    // A failure outranks a stage still marked processing: the run is not
    // coming back on its own, so the page must offer a way out.
    if (error || stages.some(st => st.status === 'error')) return 'failed';
    if (isRunning || stages.some(st => st.status === 'processing')) return 'running';
    return null;
  });

  const reason: SetupGateReason =
    automationReason ??
    // Before the plan read lands, `subscription` is null, which looks exactly
    // like "no plan" — wait rather than flash the wrong call to action.
    (!planHydrated
      ? 'checking'
      : !isSubscriptionActive(subscription)
        ? 'needs_plan'
        : 'wizard_incomplete');

  return { isGated: !hasCompletedOnboarding, reason };
}
